import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { fmt, fmtDate, fmtDateLong } from '../lib/utils';
import { todayKey, parseLocalDate } from '../lib/dateHelpers';
import { useNotify } from '../components/NotifyProvider';
import { WEDDING_PIPELINE_STAGES, STAGE_LABELS, STAGE_COLOURS, getPipelineStages } from '../lib/sampleData';
import { getWorkflowForStageChange } from '../lib/workflowEngine';
import { getPermissions } from '../lib/userSystem';
import EmailComposer from '../components/EmailComposer';
import EmailReviewModal from '../components/EmailReviewModal';
import InboundEmailModal from '../components/InboundEmailModal';
import WeddingChecklist from '../components/WeddingChecklist';
import ContractSigning from '../components/ContractSigning';
import {
  ArrowLeft,
  Phone,
  Mail,
  CheckCircle,
  Clock,
  MessageSquare,
  FileText,
  ChevronRight,
  Dot,
  Euro,
  Users,
  Edit,
  Trash2,
  Plus,
  X,
  Send,
  Zap,
} from 'lucide-react';

export default function Pipeline({ onNavigate, onSelectEvent, onSelectEventInPage, selectedEvent: propSelectedEvent, navigateAction, clearNavigateAction }) {
  const { events, updateEventStage, updateEventStageWithWorkflow, venues, addNote, updateEvent, deleteEvent, addEvent, addPayment, payments, sentEmails, workflowsEnabled, emailTemplates, addSentEmail, sendReviewedEmail, currentUser } = useAppContext();
  const { notifySuccess, notifyError } = useNotify();
  const [dateRange, setDateRange] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [newNote, setNewNote] = useState('');
  const [isEditingEvent, setIsEditingEvent] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [showAddEnquiryForm, setShowAddEnquiryForm] = useState(navigateAction === 'addWedding');

  // Auto-open add form when navigated with action
  useEffect(() => {
    if (navigateAction === 'addWedding') {
      setShowAddEnquiryForm(true);
      clearNavigateAction?.();
    }
  }, [navigateAction, clearNavigateAction]);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [composerEvent, setComposerEvent] = useState(null);
  const [showInboundModal, setShowInboundModal] = useState(false);
  // Email review modal state
  const [showEmailReview, setShowEmailReview] = useState(false);
  const [emailReviewData, setEmailReviewData] = useState(null);
  // Payment form state
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    amount: '',
    payment_date: todayKey(),
    paymentType: 'bank_transfer',
    category: 'deposit',
    description: '',
  });
  const [enquiryFormData, setEnquiryFormData] = useState({
    clientName: '',
    eventName: '',
    eventType: 'Wedding',
    date: '',
    venue: '',
    guests: '',
    pricePerHead: '',
    packageTotal: '',
    notes: '',
    phone: '',
    email: '',
  });

  // Auto-expand event when navigated from another page (e.g. Events → Pipeline)
  useEffect(() => {
    if (propSelectedEvent) {
      // Find the matching event in our events list
      const match = events.find((e) => e.id === propSelectedEvent.id);
      if (match) {
        setSelectedEvent(match);
        setActiveTab('overview');
      }
    }
  }, [propSelectedEvent, events]);

  // Filter events by date range
  const getFilteredEvents = () => {
    // Parsed via dateHelpers' local-midnight parser, not `new Date(evt.date)`
    // — the latter reads "yyyy-MM-dd" as UTC midnight, which can land an
    // event in the wrong month/year near a month boundary depending on the
    // browser's timezone offset (harmless in Ireland today, but exactly the
    // bug class dateHelpers.js exists to prevent).
    const now = parseLocalDate(todayKey());
    return events.filter((evt) => {
      if (dateRange === 'all') return true;
      const eventDate = parseLocalDate(evt.date);
      if (!eventDate) return false;

      if (dateRange === 'this_month') {
        return eventDate.getMonth() === now.getMonth() && eventDate.getFullYear() === now.getFullYear();
      }
      if (dateRange === 'next_month') {
        const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        return eventDate.getMonth() === nextMonth.getMonth() && eventDate.getFullYear() === nextMonth.getFullYear();
      }
      return true; // all
    });
  };

  // Pipeline is exclusively for weddings
  const filteredEvents = getFilteredEvents().filter((evt) => evt.type?.toLowerCase() === 'wedding');

  // Build pipeline columns using wedding stages
  const pipelineColumns = WEDDING_PIPELINE_STAGES.map((stage) => {
    const stageEvents = filteredEvents
      .filter((evt) => evt.stage === stage)
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    const revenue = stageEvents.reduce((sum, evt) => {
      return sum + (evt.packageTotal || 0) + (evt.roomHire || 0);
    }, 0);

    return {
      stage,
      label: STAGE_LABELS[stage],
      colour: STAGE_COLOURS[stage],
      events: stageEvents,
      count: stageEvents.length,
      revenue,
    };
  });

  const handleMoveToStage = (eventId, targetStage) => {
    const event = events.find((e) => e.id === eventId);
    if (!event || event.stage === targetStage) return;

    // Use the correct pipeline for this event's type
    const eventPipeline = getPipelineStages(event.type);

    // Check if there's a workflow email for this stage change (forward moves only)
    const currentIndex = eventPipeline.indexOf(event.stage);
    const targetIndex = eventPipeline.indexOf(targetStage);
    const isForward = targetIndex > currentIndex;

    if (isForward) {
      const workflow = getWorkflowForStageChange(targetStage, event.type);
      if (workflow && workflowsEnabled) {
        const template = emailTemplates?.find((t) => t.id === workflow.templateId);
        if (template && template.enabled !== false) {
          setEmailReviewData({
            event,
            template,
            workflow,
            newStage: targetStage,
          });
          setShowEmailReview(true);
          updateEventStage(eventId, targetStage);
          if (selectedEvent?.id === eventId) {
            setSelectedEvent({ ...event, stage: targetStage });
          }
          return;
        }
      }
    }

    // Move the stage (no workflow or moving backward)
    updateEventStage(eventId, targetStage);
    if (selectedEvent?.id === eventId) {
      setSelectedEvent({ ...event, stage: targetStage });
    }
  };

  // Convenience wrapper for the column arrow buttons (forward only)
  const handleMoveToNextStage = (eventId, currentStage) => {
    const event = events.find((e) => e.id === eventId);
    const eventPipeline = getPipelineStages(event?.type);
    const currentIndex = eventPipeline.indexOf(currentStage);
    if (currentIndex < eventPipeline.length - 1) {
      handleMoveToStage(eventId, eventPipeline[currentIndex + 1]);
    }
  };

  // Handle email send from review modal
  const handleReviewSend = async (emailData) => {
    await sendReviewedEmail(emailData);
  };

  // Handle skipping email from review modal
  const handleReviewSkip = ({ event, newStage }) => {
    // Stage already moved, nothing else to do
  };

  // Get workflow hint for next stage transition
  const getWorkflowHint = (event) => {
    const eventPipeline = getPipelineStages(event.type);
    const currentIndex = eventPipeline.indexOf(event.stage);
    if (currentIndex >= eventPipeline.length - 1) return null;
    const nextStage = eventPipeline[currentIndex + 1];
    try {
      const workflow = getWorkflowForStageChange(nextStage, event.type);
      if (workflow && workflowsEnabled) {
        const template = emailTemplates?.find((t) => t.id === workflow.templateId);
        return template?.name || workflow.templateId;
      }
    } catch (e) { /* graceful fallback */ }
    return null;
  };

  // Open email composer for a specific event
  const handleSendEmail = (event) => {
    setComposerEvent(event);
    setShowEmailComposer(true);
  };

  // Both helpers below compare local-midnight-to-local-midnight (via
  // dateHelpers), not raw `new Date(dateStr) - new Date()`. The old version
  // mixed a UTC-parsed date string against the current moment including
  // today's time-of-day, which can shift the displayed day count by one
  // depending on the browser's timezone and what time it is — dateHelpers.js
  // exists specifically to avoid this class of bug.
  const getDaysSinceCreated = (dateStr) => {
    const eventDate = parseLocalDate(dateStr);
    const now = parseLocalDate(todayKey());
    if (!eventDate) return 0;
    const diffTime = Math.abs(now - eventDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDaysUntilEvent = (dateStr) => {
    const eventDate = parseLocalDate(dateStr);
    const now = parseLocalDate(todayKey());
    if (!eventDate) return 'Past';
    const diffTime = eventDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? `${diffDays} days` : 'Past';
  };

  const handleSaveNote = async () => {
    if (newNote.trim() && selectedEvent) {
      const noteText = newNote.trim();
      setNewNote('');
      try {
        const savedNotes = await addNote(selectedEvent.id, noteText);
        setSelectedEvent((prev) => (prev && prev.id === selectedEvent.id ? { ...prev, notes: savedNotes } : prev));
      } catch (err) {
        notifyError('Failed to save note: ' + (err?.message || 'unknown error') + ' — please try again.');
        setNewNote(noteText);
      }
    }
  };

  const handleEditEvent = () => {
    setEditFormData({
      client: selectedEvent.client || '',
      name: selectedEvent.name || '',
      type: selectedEvent.type || '',
      date: selectedEvent.date || '',
      venue_id: selectedEvent.venue_id || '',
      guests: selectedEvent.guests || 0,
      packageTotal: selectedEvent.packageTotal || 0,
      clientPhone: selectedEvent.clientPhone || '',
      clientEmail: selectedEvent.clientEmail || '',
      brideName: selectedEvent.brideName || '',
      groomName: selectedEvent.groomName || '',
    });
    setIsEditingEvent(true);
  };

  const handleSaveEdit = async () => {
    try {
      await updateEvent(selectedEvent.id, editFormData);
    } catch (err) {
      notifyError('Failed to save changes: ' + (err?.message || 'unknown error'));
      return;
    }
    const updatedEvent = { ...selectedEvent, ...editFormData };
    setSelectedEvent(updatedEvent);
    setIsEditingEvent(false);
  };

  const permissions = getPermissions(currentUser?.role);

  const handleDeleteEvent = async () => {
    if (!permissions.canDeleteEvents) return;
    if (window.confirm(`Are you sure you want to delete the event "${selectedEvent.name}"? This action cannot be undone.`)) {
      const target = selectedEvent;
      setSelectedEvent(null);
      try {
        await deleteEvent(target.id);
      } catch (err) {
        notifyError('Failed to delete event: ' + (err?.message || 'unknown error') + ' — it has been restored.');
      }
    }
  };

  const handleRecordPayment = async () => {
    const amount = parseFloat(paymentFormData.amount);
    if (!amount || amount <= 0) {
      notifyError('Please enter a valid payment amount');
      return;
    }
    try {
      const saved = await addPayment({
        eventId: selectedEvent.id,
        amount,
        payment_date: paymentFormData.payment_date,
        paymentType: paymentFormData.paymentType,
        category: paymentFormData.category,
        description: paymentFormData.description || `Payment for ${selectedEvent.name}`,
      });
      if (saved && typeof saved.id === 'string' && saved.id.startsWith('temp_')) {
        notifyError('Payment was not saved to the database. Check your connection and try again.');
        return;
      }
      notifySuccess('Payment recorded');
    } catch (err) {
      notifyError('Failed to save payment: ' + (err?.message || 'unknown error'));
      return;
    }
    // Reset form
    setPaymentFormData({ amount: '', payment_date: todayKey(), paymentType: 'bank_transfer', category: 'deposit', description: '' });
    setShowPaymentForm(false);
  };

  // Save wedding checklist details
  const handleSaveWeddingDetails = (weddingDetails) => {
    if (!selectedEvent) return;
    updateEvent(selectedEvent.id, { weddingDetails });
    setSelectedEvent({ ...selectedEvent, weddingDetails });
  };

  // Update contract fields
  const handleContractUpdate = (contractFields) => {
    if (!selectedEvent) return;
    updateEvent(selectedEvent.id, contractFields);
    setSelectedEvent({ ...selectedEvent, ...contractFields });
  };

  const handleAddEnquiryFormChange = (e) => {
    const { name, value } = e.target;
    setEnquiryFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitEnquiry = (e) => {
    e.preventDefault();
    if (!enquiryFormData.clientName || !enquiryFormData.eventName || !enquiryFormData.date || !enquiryFormData.venue) {
      notifyError('Please fill in all required fields');
      return;
    }

    const newEnquiry = {
      client: enquiryFormData.clientName,
      name: enquiryFormData.eventName,
      type: enquiryFormData.eventType,
      date: enquiryFormData.date,
      venue_id: enquiryFormData.venue,
      guests: parseInt(enquiryFormData.guests, 10) || 0,
      pricePerHead: parseFloat(enquiryFormData.pricePerHead) || 0,
      packageTotal: parseFloat(enquiryFormData.packageTotal) || 0,
      notes: enquiryFormData.notes,
      stage: 'enquiry',
      clientPhone: enquiryFormData.phone,
      clientEmail: enquiryFormData.email,
    };

    addEvent(newEnquiry);
    setShowAddEnquiryForm(false);
    setEnquiryFormData({
      clientName: '',
      eventName: '',
      eventType: 'Wedding',
      date: '',
      venue: '',
      guests: '',
      pricePerHead: '',
      packageTotal: '',
      notes: '',
      phone: '',
      email: '',
    });
  };

  // Get sent emails for the selected event
  const eventEmails = selectedEvent
    ? (sentEmails || []).filter(e => e.eventId === selectedEvent?.id)
    : [];

  // Generate mock activity timeline
  const generateActivityTimeline = (event) => {
    const activities = [];
    const baseDate = new Date(event.date);

    activities.push({
      id: 1,
      type: 'stage_change',
      description: `Moved to ${STAGE_LABELS[event.stage]}`,
      timestamp: new Date(baseDate.getTime() - getDaysSinceCreated(event.date) * 24 * 60 * 60 * 1000),
      icon: ChevronRight,
    });

    // Real payments only — this previously invented a "Deposit recorded"
    // line just because the event had a quoted price, whether or not any
    // money had actually been taken. That sat right next to the Finance
    // tab showing the real (unpaid) balance on the same event, so staff
    // glancing at Activity — or explaining status to a client on the phone
    // — could believe a deposit was collected when it wasn't.
    const eventPayments = (payments || []).filter((p) => p.event_id === event.id);
    eventPayments.forEach((p, idx) => {
      const paymentDate = p.payment_date ? new Date(p.payment_date) : baseDate;
      activities.push({
        id: `payment-${p.id ?? idx}`,
        type: 'payment',
        description: `${(p.category || 'Payment').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())} recorded: ${fmt(p.amount || 0)}`,
        timestamp: paymentDate,
        icon: Euro,
      });
    });

    if (event.notes) {
      activities.push({
        id: 3,
        type: 'note',
        description: `Note added`,
        timestamp: new Date(baseDate.getTime() - 3 * 24 * 60 * 60 * 1000),
        icon: MessageSquare,
      });
    }

    return activities.sort((a, b) => b.timestamp - a.timestamp);
  };

  // Get other events for same client
  const getClientHistory = (clientName) => {
    return events.filter(
      (evt) => evt.client === clientName && evt.id !== selectedEvent?.id
    ).slice(0, 3);
  };

  // Detail view component
  if (selectedEvent) {
    const room = venues.find((v) => v.id === selectedEvent.venue_id);
    const mockActivityTimeline = generateActivityTimeline(selectedEvent);
    const clientHistory = getClientHistory(selectedEvent.client);
    const selectedEventPipeline = getPipelineStages(selectedEvent.type);
    const currentStageIndex = selectedEventPipeline.indexOf(selectedEvent.stage);

    // Parse notes
    const notesList = selectedEvent.notes
      ? selectedEvent.notes.split('---').map((n) => n.trim()).filter((n) => n)
      : [];

    // Get payments for this event
    const eventPayments = (payments || []).filter((p) => p.event_id === selectedEvent.id);

    // Calculate financials — use actual payments from payments array
    const totalDeposit = eventPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    const totalCost = (selectedEvent.packageTotal || 0) + (selectedEvent.roomHire || 0);
    const balanceOutstanding = totalCost - totalDeposit;

    return (
      <div className="bg-gray-50 min-h-screen p-4 md:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Back button */}
          <button
            onClick={() => setSelectedEvent(null)}
            className="flex items-center gap-2 text-emerald-700 hover:text-emerald-900 mb-4 md:mb-6 font-medium h-10 md:h-auto"
          >
            <ArrowLeft size={20} />
            Back to Pipeline
          </button>

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-0 mb-6 md:mb-8">
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 break-words">{selectedEvent.name}</h1>
              <div className="flex flex-wrap items-center gap-2 md:gap-3 mt-3">
                <span
                  className="px-3 py-1 rounded-full text-xs md:text-sm font-medium text-white"
                  style={{ backgroundColor: STAGE_COLOURS[selectedEvent.stage] }}
                >
                  {STAGE_LABELS[selectedEvent.stage]}
                </span>
                <span className="px-3 py-1 rounded-full text-xs md:text-sm font-medium bg-blue-100 text-blue-700">
                  {selectedEvent.type || 'Event'}
                </span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
              <button
                onClick={handleEditEvent}
                className="flex items-center justify-center md:justify-start gap-2 px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium text-white bg-emerald-700 hover:bg-emerald-800 transition h-10 md:h-auto"
              >
                <Edit size={16} />
                Edit
              </button>
              {permissions.canDeleteEvents && (
                <button
                  onClick={handleDeleteEvent}
                  className="flex items-center justify-center md:justify-start gap-2 px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition h-10 md:h-auto"
                >
                  <Trash2 size={16} />
                  <span className="hidden sm:inline">Delete</span>
                </button>
              )}
            </div>
          </div>

          {/* Main content grid - responsive: 1 col on mobile, 3 cols on md+ */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            {/* LEFT SIDE - Full width on mobile, 2/3 on md+ */}
            <div className="md:col-span-2 space-y-4 md:space-y-6">
              {/* Tab navigation */}
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="flex overflow-x-auto border-b border-gray-200">
                  {['overview', 'wedding', 'finance', 'contract', 'activity', 'emails'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`flex-1 px-2 md:px-4 py-2 md:py-3 text-center font-medium capitalize transition text-xs md:text-sm whitespace-nowrap ${
                        activeTab === tab
                          ? 'text-emerald-700 border-b-2 -mb-px'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      style={activeTab === tab ? { borderBottomColor: '#1A5C3A' } : {}}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                <div className="p-3 md:p-6">
                  {/* OVERVIEW TAB */}
                  {activeTab === 'overview' && (
                    <div className="space-y-4 md:space-y-6">
                      {/* Edit Event Form */}
                      {isEditingEvent && (
                        <div className="bg-amber-50 rounded-lg p-3 md:p-5 border border-amber-200">
                          <h3 className="font-bold text-sm md:text-base text-gray-900 mb-4">Edit Wedding</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Wedding Name
                              </label>
                              <input
                                type="text"
                                value={editFormData.name}
                                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                placeholder="e.g. Casey & Paul Wedding"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Bride's Name
                              </label>
                              <input
                                type="text"
                                value={editFormData.brideName}
                                onChange={(e) => setEditFormData({ ...editFormData, brideName: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Groom's Name
                              </label>
                              <input
                                type="text"
                                value={editFormData.groomName}
                                onChange={(e) => setEditFormData({ ...editFormData, groomName: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Contact / Client Name
                              </label>
                              <input
                                type="text"
                                value={editFormData.client}
                                onChange={(e) => setEditFormData({ ...editFormData, client: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Date
                              </label>
                              <input
                                type="date"
                                value={editFormData.date}
                                onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Venue
                              </label>
                              <select
                                value={editFormData.venue_id || ''}
                                onChange={(e) => setEditFormData({ ...editFormData, venue_id: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              >
                                <option value="">Select a venue</option>
                                {venues.map((venue) => (
                                  <option key={venue.id} value={venue.id}>
                                    {venue.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Guests
                              </label>
                              <input
                                type="number"
                                value={editFormData.guests}
                                onChange={(e) => setEditFormData({ ...editFormData, guests: parseInt(e.target.value) || 0 })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Quote Total
                              </label>
                              <input
                                type="number"
                                value={editFormData.packageTotal}
                                onChange={(e) => setEditFormData({ ...editFormData, packageTotal: parseFloat(e.target.value) || 0 })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Contact Phone
                              </label>
                              <input
                                type="tel"
                                value={editFormData.clientPhone}
                                onChange={(e) => setEditFormData({ ...editFormData, clientPhone: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Contact Email
                              </label>
                              <input
                                type="email"
                                value={editFormData.clientEmail}
                                onChange={(e) => setEditFormData({ ...editFormData, clientEmail: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              />
                            </div>
                          </div>
                          <div className="flex gap-3 mt-4 items-center">
                            <button
                              onClick={handleSaveEdit}
                              className="px-4 py-2 rounded-lg text-sm font-medium text-white transition"
                              style={{ backgroundColor: '#1A5C3A' }}
                            >
                              Save Changes
                            </button>
                            <button
                              onClick={() => setIsEditingEvent(false)}
                              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 transition"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Wedding Couple & Contact */}
                      <div className="bg-gray-50 rounded-lg p-3 md:p-5 border border-gray-200">
                        <h3 className="font-bold text-sm md:text-base text-gray-900 mb-3 md:mb-4">Couple & Contact</h3>
                        <div className="space-y-3">
                          {/* Bride & Groom */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <p className="text-xs md:text-sm text-gray-600">Bride</p>
                              <p className="text-base md:text-lg font-semibold text-gray-900 break-words">{selectedEvent.brideName || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs md:text-sm text-gray-600">Groom</p>
                              <p className="text-base md:text-lg font-semibold text-gray-900 break-words">{selectedEvent.groomName || '—'}</p>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs md:text-sm text-gray-600">Contact Name</p>
                            <p className="text-base md:text-lg font-semibold text-gray-900 break-words">{selectedEvent.client}</p>
                          </div>
                          <div>
                            <p className="text-xs md:text-sm text-gray-600">Phone</p>
                            <a
                              href={`tel:${selectedEvent.clientPhone}`}
                              className="text-base md:text-lg font-semibold text-emerald-700 hover:text-emerald-900 flex items-center gap-2 h-10 md:h-auto"
                            >
                              <Phone size={16} />
                              {selectedEvent.clientPhone || 'Not provided'}
                            </a>
                          </div>
                          <div>
                            <p className="text-xs md:text-sm text-gray-600">Email</p>
                            <a
                              href={`mailto:${selectedEvent.clientEmail}`}
                              className="text-base md:text-lg font-semibold text-emerald-700 hover:text-emerald-900 flex items-center gap-2 h-10 md:h-auto break-all"
                            >
                              <Mail size={16} className="flex-shrink-0" />
                              {selectedEvent.clientEmail || 'Not provided'}
                            </a>
                          </div>
                        </div>
                      </div>

                      {/* Event details */}
                      <div className="bg-gray-50 rounded-lg p-3 md:p-5 border border-gray-200">
                        <h3 className="font-bold text-sm md:text-base text-gray-900 mb-3 md:mb-4">Event Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
                          <div>
                            <p className="text-xs md:text-sm text-gray-600">Date</p>
                            <p className="text-base md:text-lg font-semibold text-gray-900">{fmtDateLong(selectedEvent.date)}</p>
                          </div>
                          <div>
                            <p className="text-xs md:text-sm text-gray-600">Time</p>
                            <p className="text-base md:text-lg font-semibold text-gray-900">{selectedEvent.time || 'TBD'}</p>
                          </div>
                          <div>
                            <p className="text-xs md:text-sm text-gray-600">Venue</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Dot size={12} style={{ color: room?.colour || '#666' }} />
                              <p className="text-base md:text-lg font-semibold text-gray-900">{room?.name || 'Unknown'}</p>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs md:text-sm text-gray-600">Guests</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Users size={16} />
                              <p className="text-base md:text-lg font-semibold text-gray-900">{selectedEvent.guests || 0}</p>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs md:text-sm text-gray-600">Setup</p>
                            <p className="text-base md:text-lg font-semibold text-gray-900 capitalize">{selectedEvent.setup || 'Standard'}</p>
                          </div>
                          <div>
                            <p className="text-xs md:text-sm text-gray-600">Equipment</p>
                            <p className="text-base md:text-lg font-semibold text-gray-900">
                              {selectedEvent.equipment ? 'Yes' : 'No'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Financial summary — clickable, links to Finance tab */}
                      <button
                        onClick={() => setActiveTab('finance')}
                        className="w-full text-left bg-gray-50 rounded-lg p-3 md:p-5 border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition group"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-bold text-sm md:text-base text-gray-900">Financial Summary</h3>
                          <span className="text-xs text-emerald-700 opacity-0 group-hover:opacity-100 transition">View Finance →</span>
                        </div>
                        {selectedEvent.pricePerHead > 0 && (
                          <p className="text-xs text-gray-500 mb-2">{fmt(selectedEvent.pricePerHead)} per head × {selectedEvent.guests || 0} guests</p>
                        )}
                        <div className="grid grid-cols-3 gap-3">
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Total Value</p>
                            <p className="text-base md:text-lg font-bold text-gray-900">{fmt(totalCost)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Paid</p>
                            <p className="text-base md:text-lg font-bold text-emerald-700">{fmt(totalDeposit)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Outstanding</p>
                            <p className={`text-base md:text-lg font-bold ${balanceOutstanding > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmt(balanceOutstanding)}</p>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <span className="text-xs text-gray-600">Status:</span>
                          {balanceOutstanding <= 0 ? (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">Paid in Full</span>
                          ) : totalDeposit > 0 ? (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">Partially Paid</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Unpaid</span>
                          )}
                          {eventPayments.length > 0 && (
                            <span className="text-xs text-gray-500 ml-auto">{eventPayments.length} payment{eventPayments.length !== 1 ? 's' : ''} recorded</span>
                          )}
                        </div>
                      </button>

                      {/* Contract status */}
                      {/* Contract status — clickable, links to Contract tab */}
                      <button
                        onClick={() => setActiveTab('contract')}
                        className="w-full text-left bg-gray-50 rounded-lg p-3 md:p-5 border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition group"
                      >
                        <div className="flex items-center justify-between">
                          <h3 className="font-bold text-sm md:text-base text-gray-900">Contract</h3>
                          <div className="flex items-center gap-2">
                            {selectedEvent.contractSignedAt ? (
                              <span className="flex items-center gap-1.5 text-emerald-700 font-medium text-sm">
                                <CheckCircle size={16} />
                                Signed
                              </span>
                            ) : selectedEvent.contractUrl ? (
                              <span className="flex items-center gap-1.5 text-amber-600 font-medium text-sm">
                                <Clock size={16} />
                                Awaiting Signature
                              </span>
                            ) : (
                              <span className="flex items-center gap-1.5 text-gray-500 font-medium text-sm">
                                <FileText size={16} />
                                Not Uploaded
                              </span>
                            )}
                            <span className="text-xs text-emerald-700 opacity-0 group-hover:opacity-100 transition">Manage →</span>
                          </div>
                        </div>
                      </button>
                    </div>
                  )}

                  {/* ACTIVITY TAB */}
                  {activeTab === 'activity' && (
                    <div className="space-y-6">
                      <div className="space-y-4 max-h-96 overflow-y-auto">
                        {mockActivityTimeline.map((activity) => {
                          const IconComponent = activity.icon;
                          return (
                            <div key={activity.id} className="flex gap-4">
                              <div className="flex flex-col items-center">
                                <div
                                  className="p-2 rounded-full bg-blue-100"
                                  style={{ color: '#1A5C3A' }}
                                >
                                  <IconComponent size={16} />
                                </div>
                                <div className="w-0.5 h-12 bg-gray-200 mt-2" />
                              </div>
                              <div className="pt-2 flex-1">
                                <p className="font-medium text-gray-900">{activity.description}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {activity.timestamp.toLocaleDateString()} at{' '}
                                  {activity.timestamp.toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Add note form */}
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Add a Note
                        </label>
                        <textarea
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          placeholder="Type your note here..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          rows={3}
                        />
                        <button
                          onClick={handleSaveNote}
                          className="mt-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition"
                          style={{ backgroundColor: '#1A5C3A' }}
                        >
                          Save Note
                        </button>
                      </div>
                    </div>
                  )}

                  {/* EMAILS TAB */}
                  {activeTab === 'emails' && (
                    <div className="space-y-4">
                      {/* Send Email button */}
                      <button
                        onClick={() => handleSendEmail(selectedEvent)}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition"
                        style={{ backgroundColor: '#1A5C3A' }}
                      >
                        <Send size={16} />
                        Send Email to {selectedEvent.client}
                      </button>

                      {/* Real sent emails for this event */}
                      {(sentEmails || []).filter(e => e.eventId === selectedEvent.id).length > 0 ? (
                        (sentEmails || [])
                          .filter(e => e.eventId === selectedEvent.id)
                          .map((email) => (
                            <div key={email.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-gray-900">{email.templateName || email.subject}</p>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      email.direction === 'inbound' ? 'bg-amber-100 text-amber-700'
                                        : email.auto ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                                    }`}>
                                      {email.direction === 'inbound' ? 'Inbound' : email.auto ? 'Auto' : 'Manual'}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1">{email.subject}</p>
                                  <p className="text-xs text-gray-500 mt-2">
                                    {email.direction === 'inbound' ? 'From' : 'To'}: {email.direction === 'inbound' ? (email.from || email.clientName) : email.to} • {new Date(email.sentAt).toLocaleDateString()} at {new Date(email.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                </div>
                                <span className={`px-3 py-1 rounded text-xs font-medium whitespace-nowrap ${
                                  email.status === 'sent' ? 'bg-green-100 text-green-700'
                                    : email.status === 'failed' ? 'bg-red-100 text-red-700'
                                    : 'bg-gray-100 text-gray-700'
                                }`}>
                                  {email.status}
                                </span>
                              </div>
                            </div>
                          ))
                      ) : (
                        <p className="text-center text-gray-500 py-6">No emails sent for this event yet. Use the button above or move to the next stage to trigger an automatic email.</p>
                      )}
                    </div>
                  )}

                  {/* WEDDING DETAILS TAB */}
                  {activeTab === 'wedding' && (
                    <WeddingChecklist event={selectedEvent} onSave={handleSaveWeddingDetails} />
                  )}

                  {/* CONTRACT TAB */}
                  {activeTab === 'contract' && (
                    <ContractSigning
                      event={selectedEvent}
                      onUpdate={handleContractUpdate}
                      onSendEmail={(emailData) => {
                        // Use the app's email sending system
                        if (addSentEmail) {
                          addSentEmail({
                            eventId: selectedEvent.id,
                            to: emailData.to,
                            subject: emailData.subject,
                            body: emailData.body,
                            sentAt: new Date().toISOString(),
                            type: 'contract',
                          });
                        }
                      }}
                    />
                  )}

                  {/* FINANCE TAB */}
                  {activeTab === 'finance' && (
                    <div className="space-y-6">
                      {/* Editable Finance Summary */}
                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <h3 className="font-bold text-sm text-gray-900 mb-4">Finance Summary</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Price Per Head (€)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={selectedEvent.pricePerHead || ''}
                              onChange={(e) => {
                                const pph = parseFloat(e.target.value) || 0;
                                const autoTotal = pph * (selectedEvent.guests || 0);
                                updateEvent(selectedEvent.id, { pricePerHead: pph, packageTotal: autoTotal });
                                setSelectedEvent({ ...selectedEvent, pricePerHead: pph, packageTotal: autoTotal });
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              placeholder={selectedEvent.guests ? `× ${selectedEvent.guests} guests` : ''}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Package Total (€)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={selectedEvent.packageTotal || ''}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                updateEvent(selectedEvent.id, { packageTotal: val });
                                setSelectedEvent({ ...selectedEvent, packageTotal: val });
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                            {selectedEvent.pricePerHead > 0 && selectedEvent.guests > 0 && (
                              <p className="text-xs text-gray-400 mt-1">{fmt(selectedEvent.pricePerHead)} × {selectedEvent.guests} = {fmt(selectedEvent.pricePerHead * selectedEvent.guests)}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Room Hire (€)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={selectedEvent.roomHire || ''}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                updateEvent(selectedEvent.id, { roomHire: val });
                                setSelectedEvent({ ...selectedEvent, roomHire: val });
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                        </div>
                        <div className="mt-4 grid grid-cols-3 gap-3 bg-gray-50 rounded-lg p-3">
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Total Value</p>
                            <p className="text-lg font-bold text-gray-900">{fmt(totalCost)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Paid</p>
                            <p className="text-lg font-bold text-emerald-700">{fmt(totalDeposit)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Outstanding</p>
                            <p className={`text-lg font-bold ${balanceOutstanding > 0 ? 'text-red-600' : 'text-emerald-700'}`}>{fmt(balanceOutstanding)}</p>
                          </div>
                        </div>
                      </div>

                      {/* Payment History */}
                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-bold text-sm text-gray-900">Payment History</h3>
                          <button
                            onClick={() => setShowPaymentForm(!showPaymentForm)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition hover:opacity-90"
                            style={{ backgroundColor: '#7C3AED' }}
                          >
                            <Plus size={14} />
                            Record Payment
                          </button>
                        </div>

                        {/* Inline payment form */}
                        {showPaymentForm && (
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Amount (€)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={paymentFormData.amount}
                                  onChange={(e) => setPaymentFormData({ ...paymentFormData, amount: e.target.value })}
                                  placeholder="0.00"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
                                <input
                                  type="date"
                                  value={paymentFormData.payment_date}
                                  onChange={(e) => setPaymentFormData({ ...paymentFormData, payment_date: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Method</label>
                                <select
                                  value={paymentFormData.paymentType}
                                  onChange={(e) => setPaymentFormData({ ...paymentFormData, paymentType: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                >
                                  <option value="bank_transfer">Bank Transfer</option>
                                  <option value="card">Card</option>
                                  <option value="cash">Cash</option>
                                  <option value="cheque">Cheque</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                                <select
                                  value={paymentFormData.category}
                                  onChange={(e) => setPaymentFormData({ ...paymentFormData, category: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                >
                                  <option value="deposit">Deposit</option>
                                  <option value="room_hire">Room Hire</option>
                                  <option value="fb_package">F&B / Package</option>
                                  <option value="final_balance">Final Balance</option>
                                  <option value="other">Other</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                                <input
                                  type="text"
                                  value={paymentFormData.description}
                                  onChange={(e) => setPaymentFormData({ ...paymentFormData, description: e.target.value })}
                                  placeholder="e.g. Deposit, 1st Payment"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2 mt-3">
                              <button
                                onClick={handleRecordPayment}
                                className="flex-1 py-2 rounded-lg text-sm font-medium text-white transition hover:opacity-90"
                                style={{ backgroundColor: '#7C3AED' }}
                              >
                                Save Payment
                              </button>
                              <button
                                onClick={() => setShowPaymentForm(false)}
                                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 transition"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}

                        {eventPayments.length > 0 ? (
                          <div className="space-y-2">
                            {eventPayments.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date)).map((p, idx) => (
                              <div key={p.id || idx} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{p.description || 'Payment'}</p>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {fmtDate(p.payment_date)} • {(p.paymentType || p.payment_type || 'bank_transfer').replace(/_/g, ' ')}
                                  </p>
                                </div>
                                <span className="text-sm font-bold text-emerald-700">{fmt(p.amount)}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-center text-gray-500 py-6 text-sm">No payments recorded yet</p>
                        )}
                      </div>

                      {/* Payment Schedule / Milestones */}
                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <h3 className="font-bold text-sm text-gray-900 mb-3">Payment Milestones</h3>
                        <div className="space-y-2">
                          {[
                            { label: 'Deposit', stage: 'deposit_paid', target: totalCost > 0 ? Math.round(totalCost * 0.1) : null },
                            { label: '1st Payment (€2,500)', stage: 'first_payment', target: 2500 },
                            { label: '2nd Payment (75%)', stage: 'second_payment', target: totalCost > 0 ? Math.round(totalCost * 0.75) : null },
                            { label: 'Final Balance', stage: 'complete', target: totalCost },
                          ].map((milestone) => {
                            const stageIdx = WEDDING_PIPELINE_STAGES.indexOf(milestone.stage);
                            const currentIdx = WEDDING_PIPELINE_STAGES.indexOf(selectedEvent.stage);
                            const isPast = currentIdx >= stageIdx;
                            const isCurrent = currentIdx === stageIdx - 1;
                            return (
                              <div key={milestone.stage} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${isPast ? 'bg-emerald-50 border-emerald-200' : isCurrent ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
                                <div className="flex items-center gap-2">
                                  {isPast ? (
                                    <CheckCircle size={16} className="text-emerald-600" />
                                  ) : (
                                    <Clock size={16} className={isCurrent ? 'text-amber-500' : 'text-gray-400'} />
                                  )}
                                  <span className={`text-sm font-medium ${isPast ? 'text-emerald-800' : 'text-gray-700'}`}>{milestone.label}</span>
                                </div>
                                {milestone.target && (
                                  <span className={`text-sm font-medium ${isPast ? 'text-emerald-700' : 'text-gray-500'}`}>{fmt(milestone.target)}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Forecast Summary */}
                      <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4">
                        <h3 className="font-bold text-sm text-emerald-900 mb-2">Forecast</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-emerald-700">Committed Revenue</p>
                            <p className="text-lg font-bold text-emerald-900">{fmt(totalCost)}</p>
                            <p className="text-xs text-emerald-600 mt-0.5">Stage: {STAGE_LABELS[selectedEvent.stage]}</p>
                          </div>
                          <div>
                            <p className="text-xs text-emerald-700">Collection Rate</p>
                            <p className="text-lg font-bold text-emerald-900">
                              {totalCost > 0 ? Math.round((totalDeposit / totalCost) * 100) : 0}%
                            </p>
                            <p className="text-xs text-emerald-600 mt-0.5">{fmt(totalDeposit)} of {fmt(totalCost)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT SIDE - Full width on mobile, 1/3 on md+ */}
            <div className="space-y-4 md:space-y-6">
              {/* Stage progression */}
              <div className="bg-white rounded-lg p-3 md:p-6 border border-gray-200">
                <h3 className="font-bold text-sm md:text-base text-gray-900 mb-4 md:mb-6">Stage Progression</h3>
                <div className="space-y-4">
                  {selectedEventPipeline.map((stage, idx) => {
                    const isCurrent = idx === currentStageIndex;
                    // Any earlier stage can still be clicked directly (a
                    // revert/correction), but a later stage can only be
                    // reached one step at a time — clicking straight from
                    // e.g. Enquiry to Complete previously skipped every
                    // workflow email tied to the stages in between, with no
                    // warning that anything had been missed.
                    const isClickable = idx < currentStageIndex || idx === currentStageIndex + 1;
                    return (
                      <button
                        key={stage}
                        onClick={() => isClickable && handleMoveToStage(selectedEvent.id, stage)}
                        className={`flex items-center gap-3 w-full text-left rounded-lg px-2 py-1.5 transition ${
                          isClickable ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
                        }`}
                        disabled={!isClickable}
                      >
                        <div>
                          {idx < currentStageIndex && (
                            <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                              <CheckCircle size={18} className="text-emerald-700" />
                            </div>
                          )}
                          {isCurrent && (
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-sm"
                              style={{ backgroundColor: STAGE_COLOURS[stage] }}
                            >
                              {idx + 1}
                            </div>
                          )}
                          {idx > currentStageIndex && (
                            <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center text-gray-400 font-bold text-sm">
                              {idx + 1}
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <p
                            className={`font-medium ${
                              isCurrent ? 'text-gray-900' : idx < currentStageIndex ? 'text-gray-700' : 'text-gray-500'
                            }`}
                          >
                            {STAGE_LABELS[stage]}
                          </p>
                        </div>
                        {isClickable && (
                          <span className="text-xs text-gray-400 opacity-0 group-hover:opacity-100">
                            {idx < currentStageIndex ? 'Revert' : 'Move'}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Forward / Back stage buttons */}
                <div className="mt-6 space-y-2">
                  {currentStageIndex < selectedEventPipeline.length - 1 && (
                    <div>
                      <button
                        onClick={() => handleMoveToStage(selectedEvent.id, selectedEventPipeline[currentStageIndex + 1])}
                        className="w-full py-3 rounded-lg text-white font-medium transition hover:opacity-90"
                        style={{ backgroundColor: '#1A5C3A' }}
                      >
                        Move to {STAGE_LABELS[selectedEventPipeline[currentStageIndex + 1]]}
                      </button>
                      {getWorkflowHint(selectedEvent) && (
                        <p className="mt-2 text-xs text-gray-500 flex items-center gap-1 justify-center">
                          <Zap size={12} className="text-amber-500" />
                          Email ready for review: {getWorkflowHint(selectedEvent)}
                        </p>
                      )}
                    </div>
                  )}
                  {currentStageIndex > 0 && (
                    <button
                      onClick={() => handleMoveToStage(selectedEvent.id, selectedEventPipeline[currentStageIndex - 1])}
                      className="w-full py-2.5 rounded-lg text-gray-700 font-medium bg-gray-100 hover:bg-gray-200 transition text-sm"
                    >
                      Move back to {STAGE_LABELS[selectedEventPipeline[currentStageIndex - 1]]}
                    </button>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-lg p-3 md:p-6 border border-gray-200">
                <h3 className="font-bold text-sm md:text-base text-gray-900 mb-3 md:mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => handleSendEmail(selectedEvent)}
                    className="w-full flex items-center justify-center md:justify-start gap-3 px-3 md:px-4 py-3 rounded-lg text-left hover:bg-emerald-50 border border-gray-200 transition min-h-[44px]"
                  >
                    <Send size={18} className="text-emerald-600 flex-shrink-0" />
                    <span className="text-xs md:text-sm font-medium text-gray-900">Send Email</span>
                  </button>
                  <button
                    onClick={() => onSelectEventInPage ? onSelectEventInPage(selectedEvent, 'functionsheets') : onNavigate?.('functionsheets')}
                    className="w-full flex items-center justify-center md:justify-start gap-3 px-3 md:px-4 py-3 rounded-lg text-left hover:bg-blue-50 border border-gray-200 transition min-h-[44px]"
                  >
                    <FileText size={18} className="text-blue-600 flex-shrink-0" />
                    <span className="text-xs md:text-sm font-medium text-gray-900 hidden sm:inline">Generate Sheet</span>
                    <span className="text-xs md:text-sm font-medium text-gray-900 sm:hidden">Sheet</span>
                  </button>
                  <button
                    onClick={() => setActiveTab('finance')}
                    className="w-full flex items-center justify-center md:justify-start gap-3 px-3 md:px-4 py-3 rounded-lg text-left hover:bg-purple-50 border border-gray-200 transition min-h-[44px]"
                  >
                    <Euro size={18} className="text-purple-600 flex-shrink-0" />
                    <span className="text-xs md:text-sm font-medium text-gray-900">Finance</span>
                  </button>
                </div>
              </div>

              {/* Client History */}
              {clientHistory.length > 0 && (
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-4">Client History</h3>
                  <div className="space-y-3">
                    {clientHistory.map((evt) => (
                      <div
                        key={evt.id}
                        onClick={() => setSelectedEvent(evt)}
                        className="p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition"
                      >
                        <p className="text-sm font-medium text-gray-900">{evt.name}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {fmtDate(evt.date)} •{' '}
                          <span
                            className="px-1.5 py-0.5 rounded text-xs text-white"
                            style={{ backgroundColor: STAGE_COLOURS[evt.stage] }}
                          >
                            {STAGE_LABELS[evt.stage]}
                          </span>
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Workflow Info */}
              {workflowsEnabled && (
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <p className="text-xs text-amber-700 flex items-center gap-2">
                    <Zap size={14} />
                    Email suggestions active. Moving stages will prompt you to review and send emails.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Email Composer Modal */}
        {showEmailComposer && (
          <EmailComposer
            isOpen={showEmailComposer}
            onClose={() => { setShowEmailComposer(false); setComposerEvent(null); }}
            events={events}
            venues={venues}
            emailTemplates={emailTemplates}
            onEmailSent={addSentEmail}
            preselectedEvent={composerEvent}
          />
        )}

        {/* Email Review Modal - shows when moving stages */}
        <EmailReviewModal
          isOpen={showEmailReview}
          onClose={() => {
            setShowEmailReview(false);
            setEmailReviewData(null);
          }}
          event={emailReviewData?.event}
          template={emailReviewData?.template}
          workflow={emailReviewData?.workflow}
          newStage={emailReviewData?.newStage}
          onSend={handleReviewSend}
          onSkip={handleReviewSkip}
        />
      </div>
    );
  }

  // ===== MAIN PIPELINE VIEW =====
  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-0 mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Wedding Pipeline</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">Track weddings from enquiry to completion</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 md:gap-3">
          {/* Date filter */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 md:px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 h-10 md:h-auto"
          >
            <option value="all">All Dates</option>
            <option value="this_month">This Month</option>
            <option value="next_month">Next Month</option>
          </select>
          <button
            onClick={() => setShowAddEnquiryForm(true)}
            className="flex items-center justify-center md:justify-start gap-2 px-3 md:px-4 py-2 rounded-lg text-sm font-medium text-white transition h-10 md:h-auto"
            style={{ backgroundColor: '#1A5C3A' }}
          >
            <Plus size={16} />
            <span className="hidden sm:inline">New Enquiry</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {/* Total potential revenue banner */}
      {(() => {
        const totalPotential = pipelineColumns.reduce((sum, col) => sum + col.revenue, 0);
        const totalWeddings = pipelineColumns.reduce((sum, col) => sum + col.count, 0);
        return (
          <div className="bg-white rounded-lg border border-gray-200 p-3 md:p-4 mb-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-xs text-gray-500">Total Weddings</p>
                <p className="text-xl font-bold text-gray-900">{totalWeddings}</p>
              </div>
              <div className="h-8 w-px bg-gray-200" />
              <div>
                <p className="text-xs text-gray-500">Potential Revenue</p>
                <p className="text-xl font-bold text-emerald-700">{fmt(totalPotential)}</p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Pipeline Columns - Horizontal on md+, Vertical on mobile */}
      <div className="hidden md:grid pb-4" style={{ gridTemplateColumns: `repeat(${pipelineColumns.length}, minmax(0, 1fr))`, gap: '0.5rem' }}>
        {pipelineColumns.map((column) => (
          <div key={column.stage} className="min-w-0">
            {/* Column Header */}
            <div
              className="rounded-t-lg px-2 py-2 text-white font-bold text-xs flex items-center justify-between"
              style={{ backgroundColor: column.colour }}
            >
              <span className="truncate">{column.label}</span>
              <span className="bg-white bg-opacity-30 px-1.5 py-0.5 rounded-full text-xs flex-shrink-0 ml-1">
                {column.count}
              </span>
            </div>

            {/* Revenue summary */}
            <div className="bg-white px-2 py-1.5 border-x border-gray-200 text-xs text-gray-600">
              <span className="font-semibold text-gray-900">{fmt(column.revenue)}</span>
            </div>

            {/* Event Cards */}
            <div className="bg-gray-100 rounded-b-lg p-1.5 space-y-1.5 min-h-[120px] border border-gray-200 border-t-0">
              {column.events.length > 0 ? (
                column.events.map((evt) => {
                  const room = venues.find((v) => v.id === evt.venue_id);
                  return (
                    <div
                      key={evt.id}
                      onClick={() => { setSelectedEvent(evt); setActiveTab('overview'); }}
                      className="bg-white rounded-lg p-2 shadow-sm border border-gray-200 hover:shadow-md transition cursor-pointer"
                    >
                      <p className="font-semibold text-xs text-gray-900 truncate">{evt.name}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-500">{fmtDate(evt.date)}</span>
                        <span className="text-xs font-medium text-gray-700">{fmt(evt.packageTotal || 0)}</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-center text-xs text-gray-400 py-8">No events</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Mobile Pipeline - Vertical Stacked View */}
      <div className="md:hidden space-y-4">
        {pipelineColumns.map((column) => (
          <div key={column.stage} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Collapsible Section Header */}
            <button
              onClick={() => {
                // Toggle expanded state for this stage
                const stageElement = document.getElementById(`stage-${column.stage}`);
                if (stageElement) {
                  stageElement.classList.toggle('hidden');
                }
              }}
              className="w-full px-4 py-3 flex items-center justify-between text-white font-bold text-sm transition hover:opacity-90"
              style={{ backgroundColor: column.colour }}
            >
              <span className="flex items-center gap-2">
                {column.label}
                <span className="bg-white bg-opacity-30 px-2 py-0.5 rounded-full text-xs font-normal">
                  {column.count}
                </span>
              </span>
              <ChevronRight size={18} className="transition transform" id={`chevron-${column.stage}`} />
            </button>

            {/* Revenue summary */}
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 text-xs text-gray-600">
              Revenue: <span className="font-semibold text-gray-900">{fmt(column.revenue)}</span>
            </div>

            {/* Event Cards - Collapsible Content */}
            <div id={`stage-${column.stage}`} className="space-y-2 p-3">
              {column.events.length > 0 ? (
                column.events.map((evt) => {
                  const room = venues.find((v) => v.id === evt.venue_id);
                  return (
                    <div
                      key={evt.id}
                      onClick={() => { setSelectedEvent(evt); setActiveTab('overview'); }}
                      className="bg-white rounded-lg p-3 shadow-sm border border-gray-200 active:shadow-md transition cursor-pointer min-h-[44px] flex flex-col justify-center"
                    >
                      <p className="font-semibold text-sm text-gray-900 truncate">{evt.name}</p>
                      <p className="text-xs text-gray-600 mt-1">{evt.client}</p>
                      <div className="flex flex-col gap-1 mt-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">{fmtDate(evt.date)}</span>
                          <span className="text-xs font-medium text-gray-700">{fmt(evt.packageTotal || 0)}</span>
                        </div>
                        {room && (
                          <div className="flex items-center gap-1">
                            <Dot size={10} style={{ color: room.colour }} />
                            <span className="text-xs text-gray-500 truncate">{room.name}</span>
                          </div>
                        )}
                        <div className="text-xs text-gray-400">
                          {getDaysUntilEvent(evt.date)}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-center text-xs text-gray-400 py-4">No events</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add Enquiry Modal */}
      {showAddEnquiryForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg md:text-xl font-bold text-gray-900">New Wedding Enquiry</h2>
              <button
                onClick={() => setShowAddEnquiryForm(false)}
                className="p-1 hover:bg-gray-100 rounded h-10 w-10 flex items-center justify-center"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmitEnquiry} className="space-y-3 md:space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Couple / Client Name *</label>
                  <input
                    type="text"
                    name="clientName"
                    value={enquiryFormData.clientName}
                    onChange={handleAddEnquiryFormChange}
                    placeholder="e.g. Sarah & James"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Wedding Name *</label>
                  <input
                    type="text"
                    name="eventName"
                    value={enquiryFormData.eventName}
                    onChange={handleAddEnquiryFormChange}
                    placeholder="e.g. Smith–Jones Wedding"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={enquiryFormData.phone}
                    onChange={handleAddEnquiryFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={enquiryFormData.email}
                    onChange={handleAddEnquiryFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Wedding Date *</label>
                  <input
                    type="date"
                    name="date"
                    value={enquiryFormData.date}
                    onChange={handleAddEnquiryFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Venue *</label>
                  <select
                    name="venue"
                    value={enquiryFormData.venue}
                    onChange={handleAddEnquiryFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    required
                  >
                    <option value="">Select venue</option>
                    {venues.map((v) => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Guests</label>
                  <input
                    type="number"
                    name="guests"
                    value={enquiryFormData.guests}
                    onChange={handleAddEnquiryFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price Per Head (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="pricePerHead"
                    value={enquiryFormData.pricePerHead}
                    onChange={(e) => {
                      const pph = parseFloat(e.target.value) || 0;
                      const autoTotal = pph * (parseInt(enquiryFormData.guests, 10) || 0);
                      setEnquiryFormData((prev) => ({ ...prev, pricePerHead: e.target.value, packageTotal: autoTotal || '' }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Package Total (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="packageTotal"
                    value={enquiryFormData.packageTotal}
                    onChange={handleAddEnquiryFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  {enquiryFormData.pricePerHead > 0 && enquiryFormData.guests > 0 && (
                    <p className="text-xs text-gray-400 mt-1">{fmt(enquiryFormData.pricePerHead)} × {enquiryFormData.guests} guests</p>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  name="notes"
                  value={enquiryFormData.notes}
                  onChange={handleAddEnquiryFormChange}
                  rows={3}
                  placeholder="How they heard about us, initial requirements, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="flex flex-col-reverse sm:flex-row gap-2 md:gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowAddEnquiryForm(false)}
                  className="px-3 md:px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 transition h-10"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 md:px-4 py-2 rounded-lg text-sm font-medium text-white transition h-10"
                  style={{ backgroundColor: '#1A5C3A' }}
                >
                  Add Wedding Enquiry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Email Composer Modal */}
      {showEmailComposer && (
        <EmailComposer
          isOpen={showEmailComposer}
          onClose={() => { setShowEmailComposer(false); setComposerEvent(null); }}
          events={events}
          venues={venues}
          emailTemplates={emailTemplates}
          onEmailSent={addSentEmail}
          preselectedEvent={composerEvent}
        />
      )}

      {/* Inbound Email Modal */}
      {showInboundModal && (
        <InboundEmailModal
          isOpen={showInboundModal}
          onClose={() => setShowInboundModal(false)}
        />
      )}

      {/* Email Review Modal - shows when moving stages */}
      <EmailReviewModal
        isOpen={showEmailReview}
        onClose={() => {
          setShowEmailReview(false);
          setEmailReviewData(null);
        }}
        event={emailReviewData?.event}
        template={emailReviewData?.template}
        workflow={emailReviewData?.workflow}
        newStage={emailReviewData?.newStage}
        onSend={handleReviewSend}
        onSkip={handleReviewSkip}
      />
    </div>
  );
}